import {createContext, type ReactNode, useContext, useEffect,} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {type Lang, LANG_LABELS, LANGS, translations,} from "./translations";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface LangCtx {
  lang: Lang;
}

const Ctx = createContext<LangCtx>({ lang: "en" });

// ---------------------------------------------------------------------------
// Provider — wraps each language route tree
// ---------------------------------------------------------------------------

export function LangProvider({
  lang,
  children,
}: {
  lang: Lang;
  children: ReactNode;
}) {
  // Sync <html lang="..."> for a11y / SEO
  useEffect(() => {
    document.documentElement.lang = lang === "pt" ? "pt-BR" : lang === "es" ? "es" : "en";
  }, [lang]);

  return <Ctx.Provider value={{ lang }}>{children}</Ctx.Provider>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Current language */
export function useLang(): Lang {
  return useContext(Ctx).lang;
}

/** Translations for the current language */
export function useT() {
  return translations[useLang()];
}

/**
 * Language URL prefix.
 * EN = "" (no prefix), PT = "/pt", ES = "/es".
 * Use as: `<Link to={lp + "/downloads"}>` or `navigate(lp + "/")`.
 */
export function useLp(): string {
  const lang = useLang();
  return lang === "en" ? "" : `/${lang}`;
}

/**
 * Returns a function that builds the equivalent URL for a target language,
 * preserving the current path (minus the old prefix).
 */
export function useLangSwitcher() {
  const lang = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  function pathFor(target: Lang): string {
    let path = location.pathname;

    // Strip existing lang prefix
    for (const l of LANGS) {
      if (l === "en") continue;
      if (path === `/${l}` || path.startsWith(`/${l}/`)) {
        path = path.slice(`/${l}`.length) || "/";
        break;
      }
    }

    const prefix = target === "en" ? "" : `/${target}`;
    return prefix + path + location.search;
  }

  function switchTo(target: Lang) {
    if (target !== lang) navigate(pathFor(target));
  }

  return { lang, pathFor, switchTo, LANGS, LANG_LABELS };
}
